import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for keyboard navigation and tile placement in the crossword puzzle.
 * 
 * Features:
 * - Arrow keys: Navigate between cells
 * - Letter keys: Sequential typing to match tiles (single, double, triple)
 * - Tab/Shift+Tab: Switch between clues
 * - Backspace/Delete: Remove tile from current cell
 * - Escape: Deselect current cell
 * - Space: Toggle direction (across/down)
 */
const useKeyboardNavigation = ({
  grid,
  tiles,
  usedTileIds,
  selectedCell,
  selectedDirection,
  onCellSelect,
  setSelectedDirection,
  handleTileClick,
  onCellDoubleClick,
  placedWords,
  isComplete,
  isPaused,
  isFlipped,
  showInstructions,
  onNextHint,
  onPrevHint
}) => {
  // Buffer for sequential letter typing
  const letterBufferRef = useRef('');
  const bufferTimeoutRef = useRef(null);
  const BUFFER_TIMEOUT = 400; // ms to wait before processing buffer

  /**
   * Find available tiles that match the letter buffer
   */
  const findMatchingTiles = useCallback((buffer, direction) => {
    if (!buffer || !tiles) return [];

    const upperBuffer = buffer.toUpperCase();
    const availableTiles = tiles.filter(tile => !usedTileIds.has(tile.id));

    return availableTiles.filter(tile => {
      const tileLetters = tile.letters.join('').toUpperCase();
      
      // Check if tile starts with the buffer
      if (!tileLetters.startsWith(upperBuffer)) return false;

      // For single tiles, direction doesn't matter
      if (tile.type === 'single') return true;

      // For multi-tiles, check direction compatibility
      const isHorizontalTile = tile.direction === 'horizontal';
      const isAcross = direction === 'across';
      
      return (isHorizontalTile && isAcross) || (!isHorizontalTile && !isAcross);
    });
  }, [tiles, usedTileIds]);

  /**
   * Check if a tile can be placed at the current position
   */
  const canPlaceTile = useCallback((tile, row, col, direction) => {
    if (!grid || row == null || col == null) return false;

    const tileLength = tile.letters.length;
    const isHorizontal = direction === 'across';

    for (let i = 0; i < tileLength; i++) {
      const checkRow = isHorizontal ? row : row + i;
      const checkCol = isHorizontal ? col + i : col;

      // Check bounds
      if (checkRow >= grid.length || checkCol >= grid[0].length) return false;
      if (checkRow < 0 || checkCol < 0) return false;

      const cell = grid[checkRow][checkCol];
      if (!cell || cell.isBlocked || cell.isLocked || cell.letter) return false;
    }

    return true;
  }, [grid]);

  /**
   * Find the best matching tile to place
   */
  const findBestTile = useCallback((buffer) => {
    if (!selectedCell || !selectedDirection) return null;

    const { row, col } = selectedCell;
    const matchingTiles = findMatchingTiles(buffer, selectedDirection);

    if (matchingTiles.length === 0) return null;

    // Sort by:
    // 1. Exact match first
    // 2. Then by length (longer tiles preferred)
    // 3. Then by whether they can actually be placed
    const sortedTiles = matchingTiles
      .map(tile => ({
        tile,
        isExactMatch: tile.letters.join('').toUpperCase() === buffer.toUpperCase(),
        canPlace: canPlaceTile(tile, row, col, selectedDirection)
      }))
      .filter(({ canPlace }) => canPlace)
      .sort((a, b) => {
        // Exact matches first
        if (a.isExactMatch && !b.isExactMatch) return -1;
        if (!a.isExactMatch && b.isExactMatch) return 1;
        // Then by length (prefer tiles that match the buffer length)
        const aLenDiff = Math.abs(a.tile.letters.length - buffer.length);
        const bLenDiff = Math.abs(b.tile.letters.length - buffer.length);
        return aLenDiff - bLenDiff;
      });

    return sortedTiles.length > 0 ? sortedTiles[0].tile : null;
  }, [selectedCell, selectedDirection, findMatchingTiles, canPlaceTile]);

  /**
   * Process the letter buffer and place matching tile
   * @param {Object} overrideCell - Optional cell to use instead of selectedCell
   * @returns {Object} - Object with placed tile info and next cell position
   */
  const processBuffer = useCallback((overrideCell = null) => {
    const buffer = letterBufferRef.current;
    if (!buffer) return null;

    // Use override cell if provided, otherwise use selectedCell
    const cellToUse = overrideCell || selectedCell;
    if (!cellToUse || !selectedDirection) {
      letterBufferRef.current = '';
      return null;
    }

    // Temporarily override selectedCell for findBestTile
    const tile = (() => {
      const { row, col } = cellToUse;
      const matchingTiles = findMatchingTiles(buffer, selectedDirection);
      if (matchingTiles.length === 0) return null;

      const sortedTiles = matchingTiles
        .map(tile => ({
          tile,
          isExactMatch: tile.letters.join('').toUpperCase() === buffer.toUpperCase(),
          canPlace: canPlaceTile(tile, row, col, selectedDirection)
        }))
        .filter(({ canPlace }) => canPlace)
        .sort((a, b) => {
          if (a.isExactMatch && !b.isExactMatch) return -1;
          if (!a.isExactMatch && b.isExactMatch) return 1;
          const aLenDiff = Math.abs(a.tile.letters.length - buffer.length);
          const bLenDiff = Math.abs(b.tile.letters.length - buffer.length);
          return aLenDiff - bLenDiff;
        });

      return sortedTiles.length > 0 ? sortedTiles[0].tile : null;
    })();

    if (tile) {
      handleTileClick(tile, cellToUse);
      
      // Calculate next cell position after placing this tile
      const steps = tile.letters.length;
      const { row, col } = cellToUse;
      let nextRow = row;
      let nextCol = col;
      
      if (selectedDirection === 'across') {
        nextCol += steps;
      } else {
        nextRow += steps;
      }

      // Clear the buffer
      letterBufferRef.current = '';
      
      return {
        tile,
        nextCell: { row: nextRow, col: nextCol }
      };
    }

    // Clear the buffer
    letterBufferRef.current = '';
    return null;
  }, [selectedCell, selectedDirection, findMatchingTiles, canPlaceTile, handleTileClick]);

  /**
   * Add letter to buffer and set timeout
   */
  const addToBuffer = useCallback((letter) => {
    // Clear existing timeout
    if (bufferTimeoutRef.current) {
      clearTimeout(bufferTimeoutRef.current);
    }

    const upperLetter = letter.toUpperCase();
    const currentBuffer = letterBufferRef.current;
    const combinedBuffer = currentBuffer + upperLetter;

    // Check if combined buffer could match any tiles
    const combinedMatches = findMatchingTiles(combinedBuffer, selectedDirection);
    
    // If the combined buffer doesn't match any tiles AND we have something in the buffer,
    // process the current buffer first, then start fresh with the new letter
    if (combinedMatches.length === 0 && currentBuffer.length > 0) {
      // Process the existing buffer and get the next cell position
      const firstPlacement = processBuffer();
      
      // Start fresh with the new letter
      letterBufferRef.current = upperLetter;
      
      // Check if this single letter has any potential matches
      const singleMatches = findMatchingTiles(upperLetter, selectedDirection);
      
      if (singleMatches.length === 0) {
        // No matches for this letter either, clear buffer
        letterBufferRef.current = '';
        return;
      }
      
      // Check if we should process immediately (single letter with no longer matches)
      const longerMatches = singleMatches.filter(t => t.letters.length > 1);
      if (longerMatches.length === 0) {
        // Use the next cell position from the first placement
        if (firstPlacement && firstPlacement.nextCell) {
          // Update selectedCell to the next position, then place the tile
          onCellSelect(firstPlacement.nextCell);
          // Delay slightly to allow React state to update, then place at the calculated position
          requestAnimationFrame(() => {
            setTimeout(() => {
              processBuffer(firstPlacement.nextCell);
            }, 10);
          });
        } else {
          // Fallback: delay and use current selectedCell
          requestAnimationFrame(() => {
            setTimeout(() => {
              processBuffer();
            }, 10);
          });
        }
        return;
      }
      
      // Set timeout for the new single-letter buffer
      bufferTimeoutRef.current = setTimeout(() => {
        processBuffer();
      }, BUFFER_TIMEOUT);
      return;
    }

    // Combined buffer has matches, add the letter normally
    letterBufferRef.current = combinedBuffer;

    // Check if we have an exact match for the current buffer
    const exactMatchTile = findBestTile(combinedBuffer);
    
    // If buffer is 3 chars, process immediately (max tile size)
    if (combinedBuffer.length >= 3) {
      processBuffer();
      return;
    }

    // If we have an exact match and no longer tiles could match, process immediately
    if (exactMatchTile && exactMatchTile.letters.length === combinedBuffer.length) {
      // Check if any longer tiles could still match
      const longerMatches = combinedMatches.filter(t => t.letters.length > combinedBuffer.length);
      
      if (longerMatches.length === 0) {
        processBuffer();
        return;
      }
    }

    // Set timeout to process buffer
    bufferTimeoutRef.current = setTimeout(() => {
      processBuffer();
    }, BUFFER_TIMEOUT);
  }, [processBuffer, findBestTile, findMatchingTiles, selectedDirection, onCellSelect]);

  /**
   * Navigate to adjacent cell
   */
  const navigateToCell = useCallback((deltaRow, deltaCol) => {
    if (!grid || !selectedCell) return;

    let newRow = selectedCell.row + deltaRow;
    let newCol = selectedCell.col + deltaCol;

    // Find next valid cell in direction
    while (
      newRow >= 0 && newRow < grid.length &&
      newCol >= 0 && newCol < grid[0].length
    ) {
      const cell = grid[newRow][newCol];
      if (!cell.isBlocked) {
        onCellSelect({ row: newRow, col: newCol });
        return;
      }
      newRow += deltaRow;
      newCol += deltaCol;
    }
  }, [grid, selectedCell, onCellSelect]);

  /**
   * Check if there are open cells in a given direction from a cell
   */
  const hasOpenCellsInDirection = useCallback((row, col, direction) => {
    if (!grid) return false;

    if (direction === 'across') {
      // Check left
      if (col > 0) {
        const leftCell = grid[row][col - 1];
        if (!leftCell.isBlocked && !leftCell.letter && !leftCell.isLocked) return true;
      }
      // Check right
      if (col < grid[0].length - 1) {
        const rightCell = grid[row][col + 1];
        if (!rightCell.isBlocked && !rightCell.letter && !rightCell.isLocked) return true;
      }
    } else {
      // Check above
      if (row > 0) {
        const aboveCell = grid[row - 1][col];
        if (!aboveCell.isBlocked && !aboveCell.letter && !aboveCell.isLocked) return true;
      }
      // Check below
      if (row < grid.length - 1) {
        const belowCell = grid[row + 1][col];
        if (!belowCell.isBlocked && !belowCell.letter && !belowCell.isLocked) return true;
      }
    }

    return false;
  }, [grid]);

  /**
   * Toggle direction between across and down
   */
  const toggleDirection = useCallback(() => {
    if (!selectedCell || !grid) return;

    const { row, col } = selectedCell;
    const newDirection = selectedDirection === 'across' ? 'down' : 'across';
    
    // Only toggle if there are open cells in the new direction
    if (hasOpenCellsInDirection(row, col, newDirection)) {
      setSelectedDirection(newDirection);
    }
  }, [selectedCell, grid, selectedDirection, setSelectedDirection, hasOpenCellsInDirection]);

  /**
   * Remove tile from current cell
   */
  const removeTileFromCurrentCell = useCallback(() => {
    if (!selectedCell || !grid) return;

    const { row, col } = selectedCell;
    const cell = grid[row][col];

    if (cell.tileId != null && !cell.isLocked) {
      onCellDoubleClick(row, col);
    }
  }, [selectedCell, grid, onCellDoubleClick]);

  /**
   * Main keyboard event handler
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't handle if game is paused, complete, flipped, or showing instructions
      if (isComplete || isPaused || isFlipped || showInstructions) return;

      // Don't handle if typing in an input/textarea
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') return;

      const { key, shiftKey } = event;

      // Letter keys (A-Z)
      if (/^[a-zA-Z]$/.test(key)) {
        if (!selectedCell) return;
        event.preventDefault();
        addToBuffer(key);
        return;
      }

      // Arrow keys
      if (key === 'ArrowUp') {
        event.preventDefault();
        if (selectedCell) {
          navigateToCell(-1, 0);
        }
        return;
      }

      if (key === 'ArrowDown') {
        event.preventDefault();
        if (selectedCell) {
          navigateToCell(1, 0);
        }
        return;
      }

      if (key === 'ArrowLeft') {
        event.preventDefault();
        if (selectedCell) {
          navigateToCell(0, -1);
        }
        return;
      }

      if (key === 'ArrowRight') {
        event.preventDefault();
        if (selectedCell) {
          navigateToCell(0, 1);
        }
        return;
      }

      // Tab - switch between clues
      if (key === 'Tab') {
        event.preventDefault();
        if (shiftKey) {
          onPrevHint && onPrevHint();
        } else {
          onNextHint && onNextHint();
        }
        return;
      }

      // Space - toggle direction
      if (key === ' ') {
        event.preventDefault();
        toggleDirection();
        return;
      }

      // Backspace or Delete - remove tile
      if (key === 'Backspace' || key === 'Delete') {
        event.preventDefault();
        // Clear buffer first
        if (letterBufferRef.current) {
          letterBufferRef.current = '';
          if (bufferTimeoutRef.current) {
            clearTimeout(bufferTimeoutRef.current);
          }
        } else {
          removeTileFromCurrentCell();
        }
        return;
      }

      // Escape - deselect
      if (key === 'Escape') {
        event.preventDefault();
        onCellSelect(null);
        letterBufferRef.current = '';
        if (bufferTimeoutRef.current) {
          clearTimeout(bufferTimeoutRef.current);
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (bufferTimeoutRef.current) {
        clearTimeout(bufferTimeoutRef.current);
      }
    };
  }, [
    isComplete,
    isPaused,
    isFlipped,
    showInstructions,
    selectedCell,
    addToBuffer,
    navigateToCell,
    toggleDirection,
    removeTileFromCurrentCell,
    onCellSelect,
    onNextHint,
    onPrevHint
  ]);

  // Return the current buffer for visual feedback (optional)
  return {
    letterBuffer: letterBufferRef.current
  };
};

export default useKeyboardNavigation;

